import { nextTick } from 'vue';
import { fetchUserUnreadPosts } from '@/services/apiServices/postService';
import { toApiTag } from '@/helpers/tagsHelper';
import { authErrorHandler } from '@/services/errorServices/authHandler.js';
import { triggerSessionExpired } from '@/services/errorServices/sessionExpired';
import { useAPI } from '@/services/useAPI.js';
import { getUserFriendlyErrorMessage } from '@/helpers/errorHandlerHelper.js';
import eventBus from '@/helpers/eventBusHelper';
import { debugNotify, debugNotifyJSON } from '@/helpers/debugNotifyHelper';
import { trackAnalyticsEvent } from '@/services/firebase';

// GA4 brand dimension — see docs/GOOGLE_ANALYTICS_TRACKING_PLAN.md.
const analyticsBrand = () => import.meta.env.VITE_THEME_PRESET;

/**
 * Vuex actions for the "home" module.
 *
 * Fetches posts/profiles/search results from the API and commits results via
 * updates.js mutations. Also hosts module-local helpers (below) for feed
 * caching, concurrency-limited fetching, and thread/reply enrichment that
 * back the exported action map at the bottom of the file.
 */

// --- Performance helpers for faster first paint of feeds ---
const FEED_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes TTL to avoid staleness

/** Read a previously cached feed array from localStorage if present and not expired. */
function readFeedCache(key, ttlMs = FEED_CACHE_TTL_MS) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (!ts || Date.now() - ts > ttlMs) return null;
    return Array.isArray(data) ? data : null;
  } catch (_) {
    return null;
  }
}

/** Persist a feed array to localStorage under `key`, timestamped for later TTL checks. */
function writeFeedCache(key, data) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ ts: Date.now(), data: Array.isArray(data) ? data : [] })
    );
  } catch (_) {
    // ignore quota or serialization issues
  }
}

/**
 * Map `items` through async `mapper` with at most `limit` in flight at once.
 * A mapper rejection resolves that item's slot to `undefined` rather than
 * failing the whole batch.
 */
async function mapWithConcurrency(items, limit, mapper) {
  const ret = new Array(items.length);
  let nextIndex = 0;
  const runners = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
    while (nextIndex < items.length) {
      const i = nextIndex++;
      try {
        ret[i] = await mapper(items[i], i);
      } catch (e) {
        ret[i] = undefined;
      }
    }
  });
  await Promise.all(runners);
  return ret;
}

// Lightweight user fetch with caching for synthesizing reaction comments
const userBriefCache = new Map();
/** Fetch a brief user record by id, memoized in-process (including negative/null results). */
async function fetchUserBriefCached(userId) {
  if (!userId) return null;
  if (userBriefCache.has(userId)) return userBriefCache.get(userId);
  try {
    const res = await fetch(`/api/users/${userId}`);
    if (!res.ok) {
      userBriefCache.set(userId, null);
      return null;
    }
    const data = await res.json();
    userBriefCache.set(userId, data || null);
    return data || null;
  } catch (_) {
    userBriefCache.set(userId, null);
    return null;
  }
}

/**
 * Build synthetic "reply" objects from a post's `reactionSets` entries that
 * carry a non-empty `description` (a reaction-with-comment), so historical
 * reaction comments render alongside real replies via the standard
 * ReplyPostBox/reply-post components. Skips entries whose author can't be
 * resolved. Returns at most `opts.max` (default 3).
 */
async function synthesizeReactionCommentsFromPost(post, opts = {}) {
  try {
    const { max = 3 } = opts;
    const sets = Array.isArray(post?.reactionSets) ? post.reactionSets : [];
    const withComments = sets.filter(
      (s) => typeof s?.description === 'string' && s.description.trim()
    );
    if (!withComments.length) return [];

    const taken = [];
    for (let i = 0; i < withComments.length && taken.length < max; i++) {
      const s = withComments[i];
      const user = await fetchUserBriefCached(s.userId);
      // Require minimal author data to render standard Reply Post component
      if (!user) continue;
      const createdAt = s.createdAt || post.publishedAt || new Date().toISOString();
      // Stable synthetic id to allow dedupe
      const id = `rxn-cmt:${post.id}:${s.userId}:${new Date(createdAt).getTime()}`;
      taken.push({
        id,
        type: 'reply',
        text: s.description.trim(),
        rawText: s.description.trim(),
        authorUserId: user.id,
        authorUser: user,
        channel: post.channel || null,
        displayedChannel: post.displayedChannel || null,
        displayedChannelId: post.displayedChannelId || null,
        repostedPostId: post.id,
        repostedPost: { id: post.id, type: post.type },
        // Provide the full original post so ReplyPostBox can render the parent
        originalPost: post,
        // Mark as a reply in feed context for downstream render logic/keys
        isReplyInFeed: true,
        publishedAt: createdAt,
        createdAt: createdAt,
        isReactionComment: true,
      });
    }
    return taken;
  } catch (e) {
    if (import.meta.env.MODE === 'development') {
      console.debug('[synthesizeReactionCommentsFromPost] failed', e?.message);
    }
    return [];
  }
}

/**
 * Thread standalone `type === 'reply'` posts under their parent post, producing
 * a `{ ...parentPost, isReplyThread: true, reply, replyData }` structure for
 * posts with a resolvable parent, or a standalone `isReplyThread: false` post
 * otherwise. Currently unused by the exported actions below (kept for reuse).
 */
function processFollowingPostsWithReplies(posts) {
  const processedPosts = [];
  const postMap = new Map(); // Map to store posts by ID
  const replies = [];

  // First pass: separate posts and replies
  posts.forEach(post => {
    if (post.type === 'reply' && post.repostedPost) {
      replies.push(post);
    } else {
      postMap.set(post.id, post);
      processedPosts.push(post);
    }
  });

  // Second pass: attach replies to their parent posts
  replies.forEach(reply => {
    const parentPostId = reply.repostedPost.id;
    const parentPost = postMap.get(parentPostId);
    
    if (parentPost) {
      // Create a threaded post structure
      const threadedPost = {
        ...parentPost,
        isReplyThread: true,
        reply: reply,
        // Store the reply separately for the ReplyPostBox component
        replyData: reply
      };
      
      // Replace the original post with the threaded version
      const parentIndex = processedPosts.findIndex(p => p.id === parentPostId);
      if (parentIndex !== -1) {
        processedPosts[parentIndex] = threadedPost;
      }
    } else {
      // If parent post not found, add reply as standalone
      processedPosts.push({
        ...reply,
        isReplyThread: false
      });
    }
  });

  return processedPosts;
}

/**
 * For each non-reply post, fetch its server replies plus synthesized
 * reaction-comments, dedupe by author+normalized text, and attach the merged
 * list as `integratedReplies`/`totalRepliesCount` (`hasIntegratedReplies:
 * true`). Runs with `opts.concurrency` (default 4) fetches in flight, and
 * filters out standalone reply posts once their content has been folded
 * into a parent's `integratedReplies`.
 */
async function enhancePostsWithReplies(posts, opts = {}) {
  const { concurrency = 4 } = opts;
  const integratedReplyIds = new Set();

  // Prepare mapping tasks only for non-reply posts; keep replies as-is
  const tasks = posts.map(async (post) => {
    if (post.type === 'reply') return post;
    try {
      const repliesResponse = await fetch(`/api/posts/${post.id}/replies`);
      if (!repliesResponse.ok) {
        // Log 503 and other HTTP errors but don't break the flow
        if (repliesResponse.status === 503) {
          if (import.meta.env.MODE === 'development') {
            console.warn(`[enhancePostsWithReplies] Service unavailable for post ${post.id} replies (503)`);
          }
        } else {
          console.warn(`[enhancePostsWithReplies] Failed to fetch replies for post ${post.id}: ${repliesResponse.status}`);
        }
        return post;
      }
      const repliesData = await repliesResponse.json();
      const serverReplies = Array.isArray(repliesData.results) ? repliesData.results.slice().reverse() : [];
      // Stopgap: add historical reaction comments with description
      const synthetic = await synthesizeReactionCommentsFromPost(post, { max: 3 });
      const deduped = [...serverReplies];
      const normalize = (t) => (t || '').toString().toLowerCase().replace(/\s+/g, ' ').trim();
      const seenKeys = new Set(serverReplies.map(r => `${r.authorUserId}|${normalize(r.text)}`));
      for (const s of synthetic) {
        const key = `${s.authorUserId}|${normalize(s.text)}`;
        if (!seenKeys.has(key)) {
          deduped.push(s);
          seenKeys.add(key);
        }
      }
      if (deduped.length > 0) {
        deduped.forEach((reply) => {
          if (reply.id) integratedReplyIds.add(reply.id);
        });
        return {
          ...post,
          hasIntegratedReplies: true,
          integratedReplies: deduped,
          totalRepliesCount: post.numReplies || repliesData.total || deduped.length,
        };
      }
      return post;
    } catch (error) {
      // Catch network errors, timeouts, and other fetch failures
      if (import.meta.env.MODE === 'development') {
        console.debug(`[enhancePostsWithReplies] Network error for post ${post.id}:`, error.message || error);
      }
      return post;
    }
  });

  // Execute with concurrency cap
  const enhanced = await mapWithConcurrency(tasks, concurrency, (task) => task);
  // Filter out standalone replies that we integrated into parents
  return enhanced.filter((p) => !(p?.type === 'reply' && integratedReplyIds.has(p.id)));
}

/**
 * Interleave a post's top replies into the feed array right after it:
 * replies from users the current user follows are threaded in (up to 2,
 * followed-first then by engagement); if none are from followed users, the
 * single highest-engagement reply is shown instead for discovery. Currently
 * unused by the exported actions below (kept for reuse).
 */
async function applyHybridReplyStrategy(posts, rootState) {
  // Get current user's following list (this would ideally come from the store)
  const userFollowingIds = rootState?.user?.following || [];
  const enhancedPosts = [];
  const processedPostIds = new Set(); // Track posts we've already added
  
  for (const post of posts) {
    // Skip if we've already processed this post as part of a reply thread
    if (processedPostIds.has(post.id)) continue;
    
    enhancedPosts.push(post);
    processedPostIds.add(post.id);
    
    // If this is a regular post, check for relevant replies
    if (post.type !== 'reply' && !post.isReplyInFeed) {
      try {
        // Fetch replies for this post
        const repliesResponse = await fetch(`/api/posts/${post.id}/replies?limit=5`);
        if (!repliesResponse.ok) {
          if (repliesResponse.status === 503) {
            if (import.meta.env.MODE === 'development') {
              console.warn(`[applyHybridReplyStrategy] Service unavailable for post ${post.id} replies (503)`);
            }
          } else {
            console.warn(`[applyHybridReplyStrategy] Failed to fetch replies for post ${post.id}: ${repliesResponse.status}`);
          }
          continue;
        }
        
        const repliesData = await repliesResponse.json();
        
        if (repliesData.results && repliesData.results.length > 0) {
          // Apply hybrid strategy: prioritize replies from followed users
          const sortedReplies = repliesData.results.sort((a, b) => {
            const aFromFollowed = userFollowingIds.includes(a.authorUserId);
            const bFromFollowed = userFollowingIds.includes(b.authorUserId);
            
            // Prioritize replies from followed users
            if (aFromFollowed && !bFromFollowed) return -1;
            if (!aFromFollowed && bFromFollowed) return 1;
            
            // Then prioritize by engagement (reactions count)
            const aEngagement = (a.reactions?.total || 0) + (a.repliesCount || 0);
            const bEngagement = (b.reactions?.total || 0) + (b.repliesCount || 0);
            
            return bEngagement - aEngagement;
          });
          
          // Check if we have replies from followed users
          const repliesFromFollowed = sortedReplies.filter(reply => 
            userFollowingIds.includes(reply.authorUserId)
          );
          
          if (repliesFromFollowed.length > 0) {
            // If we have replies from followed users, show them as a thread with the original post
            const threadReplies = repliesFromFollowed.slice(0, 2).map(reply => ({
              ...reply,
              isReplyInThread: true,
              parentPost: {
                id: post.id,
                title: post.title,
                authorUsername: post.authorUsername,
                authorDisplayName: post.authorDisplayName,
                authorAvatar: post.authorAvatar,
                publishedAt: post.publishedAt,
                reactions: post.reactions,
                text: post.text
              }
            }));
            
            enhancedPosts.push(...threadReplies);
          } else {
            // Include top 1 high-engagement reply from anyone for discovery
            const topReplies = sortedReplies.slice(0, 1).map(reply => ({
              ...reply,
              isReplyInFeed: true,
              parentPost: {
                id: post.id,
                title: post.title,
                authorUsername: post.authorUsername,
                authorDisplayName: post.authorDisplayName
              }
            }));
            
            enhancedPosts.push(...topReplies);
          }
        }
      } catch (error) {
        if (import.meta.env.MODE === 'development') {
          console.debug(`[applyHybridReplyStrategy] Network error for post ${post.id}:`, error.message || error);
        }
      }
    }
  }
  
  return enhancedPosts;
}

export default {
  /** Push a fresh `reactionSets` value for `postId` into the home and org post collections. */
  async updatePostReactions({ commit }, { postId, reactionSets }) {
    commit("updatePostReactions", { postId, reactionSets });
    commit("updateOrgPostReactions", { postId, reactionSets });
  },
  // Refresh integrated replies for a post in feeds (Following/Recommended)
  async refreshIntegratedReplies({ commit }, postId) {
    if (!postId || postId === 'null' || postId === 'undefined') return;
    try {
      const repliesResponse = await fetch(`/api/posts/${postId}/replies`);
      if (!repliesResponse.ok) {
        if (repliesResponse.status === 503) {
          if (import.meta.env.MODE === "development") {
            console.warn(
              `[refreshIntegratedReplies] Service unavailable for post ${postId} (503)`
            );
          }
        } else {
          console.warn(
            `[refreshIntegratedReplies] Failed to fetch replies for post ${postId}: ${repliesResponse.status}`
          );
        }
        return;
      }
      const repliesData = await repliesResponse.json();
      const results = Array.isArray(repliesData.results)
        ? repliesData.results.slice().reverse()
        : [];

      // OLD: only used raw server replies — a reaction+comment (stored
      // only as reactionSets[].description, no real Post row) never showed
      // up in the integrated-replies preview this powers, even right after
      // reacting, since nothing here looked at the post's own reactionSets.
      // const repliesPayload = {
      //   postId,
      //   replies: results,
      //   total: repliesData.total || results.length,
      // };
      let merged = results;
      try {
        const postRes = await fetch(`/api/posts/${postId}`);
        const postObj = await postRes.json();
        const synthetic = await synthesizeReactionCommentsFromPost(postObj, { max: 10 });
        const normalize = (t) => (t || "").toString().toLowerCase().replace(/\s+/g, " ").trim();
        const seen = new Set(results.map((r) => `${r.authorUserId}|${normalize(r.text)}`));
        merged = [...results];
        for (const s of synthetic) {
          const key = `${s.authorUserId}|${normalize(s.text)}`;
          if (!seen.has(key)) {
            merged.push(s);
            seen.add(key);
          }
        }
      } catch (_) {
        // best-effort — fall back to server replies only
      }
      const repliesPayload = {
        postId,
        replies: merged,
        total: (repliesData.total || results.length) + (merged.length - results.length),
      };
      commit("setIntegratedRepliesForPost", repliesPayload);
      commit("updateOrgIntegratedReplies", repliesPayload);
    } catch (e) {
      if (import.meta.env.MODE === "development") {
        console.debug("[refreshIntegratedReplies] network error:", e?.message);
      }
    }
  },
  /**
   * Load the Recommended feed (optionally filtered by `payload.data.label`/`payload.label` tag).
   * Warm-starts from a short-lived localStorage cache for instant first paint, commits the base
   * API results, then hydrates replies in the background and re-commits when ready.
   */
  async setRecommendedPosts({ commit, rootState }, payload) {
    // const params = new URLSearchParams({
    //   limit: "30",
    //   showOwnPosts: "true",
    //   includeReplies: "true", // Enable hybrid reply strategy
    // });
    const params = new URLSearchParams({
      limit: "10",
      showOwnPosts: "true",
      includeReplies: "true", // Enable hybrid reply strategy
    });
    if (payload?.data?.label && payload?.data?.label !== "All") {
      params.set("tag", toApiTag(payload.data.label));
    } else if (payload?.label && payload?.label !== "All") {
      params.set("tag", toApiTag(payload.label));
    }

    try {
      // 0) Instant warm start via cache (if present)
      const cacheKey = "feed:recommended:all";
      const cached = readFeedCache(cacheKey);
      if (cached && cached.length) {
        // Render immediately to avoid spinner while network fetches fresh data
        commit("setRecommendedPosts", cached);
      }

      let response = await fetch(`/api/posts/recommended?${params.toString()}`);

      if (!response.ok) {
        const friendlyMessage = getUserFriendlyErrorMessage({
          status: response.status,
          statusText: response.statusText,
          message: `HTTP ${response.status}: ${response.statusText}`,
        });
        console.error("Error fetching recommended posts:", friendlyMessage);
        return;
      }

      let posts = await response.json();

      if (posts.statusCode) {
        authErrorHandler(posts);
        return;
      }

      if (posts.results) {
        // 1) Commit base results immediately for first paint
        commit("setRecommendedPosts", posts.results);
        writeFeedCache(cacheKey, posts.results);

        // 2) Hydrate replies in background and update when ready (non-blocking)
        enhancePostsWithReplies(posts.results, { concurrency: 6 })
          .then(async (enhanced) => {
            await nextTick();
            commit("setRecommendedPosts", enhanced);
            writeFeedCache(cacheKey, enhanced);
          })
          .catch((e) =>
            console.debug(
              "enhancePostsWithReplies failed (recommended):",
              e?.message
            )
          );
      } else {
        if (import.meta.env.MODE === "development") {
          console.warn("No recommended posts found.");
        }
      }

      return posts;
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      console.error("Error fetching recommended posts:", friendlyMessage);

      // In development, also log the original error for debugging
      if (import.meta.env.MODE === "development") {
        console.error("Original error:", error);
      }
    }
  },

  /**
   * Fetch the next page for a given feed/profile tab (`payload.tab`: recommendedPosts,
   * followingPosts, onlyUserPosts, userReplies, userReposts, unreadPosts) using
   * `payload.lastId` as the cursor. Returns the raw API response; does not commit —
   * callers merge pages via the `setCollection` mutation.
   */
  async setNextPagonationPosts({ commit, rootState }, payload) {
    if (!payload) {
      throw new Error("Payload is missing or invalid");
    }

    // const params = new URLSearchParams({
    //   limit: "15",
    //   after: payload.lastId,
    // });
    const params = new URLSearchParams({
      limit: "10",
      after: payload.lastId,
    });

    let response;
    let posts;
    try {
      switch (payload.tab) {
        case "recommendedPosts":
          params.set("showOwnPosts", "true");
          params.set("includeReplies", "true"); // Enable hybrid reply strategy for pagination
          if (payload?.filter && payload?.filter !== "All")
            params.set("tag", toApiTag(payload?.filter));
          response = await fetch(`/api/posts/recommended?${params.toString()}`);
          posts = await response.json();

          // Apply integrated reply enhancement (with reaction comment synthesis) to paginated posts as well
          if (posts.results) {
            posts.results = await enhancePostsWithReplies(posts.results);
          }
          break;
        case "followingPosts":
          if (
            payload?.filter &&
            payload?.filter !== "All" &&
            payload?.filter !== "Following" &&
            payload?.filter !== "Monitoring"
          )
            params.set("tag", toApiTag(payload?.filter));
          if (payload?.filter === "Following") {
            params.set("only", "positive");
          }
          if (payload?.filter === "Monitoring") {
            params.set("only", "negative");
          }
          let postType;
          if (payload?.type?.type !== "Reply") {
            postType = "&type=post&type=quote&type=repost";
          } else {
            postType = "&type=reply";
          }
          response = await fetch(
            `/api/posts/for-you?${params.toString()}${postType}`
          );
          posts = await response.json();
          break;
        case "onlyUserPosts":
          params.set("channelId", payload.channelId);
          params.set("onlyPosts", "true");
          response = await fetch(`/api/posts?${params.toString()}`);
          posts = await response.json();
          break;
        case "userReplies":
          response = await fetch(
            `/api/posts?channelId=${payload.channelId}&type=reply&${params.toString()}`
          );
          posts = await response.json();
          break;
        case "userReposts":
          response = await fetch(
            `/api/posts?channelId=${payload.channelId}&type=repost&${params.toString()}`
          );
          posts = await response.json();
          break;
        case "unreadPosts":
          response = await fetchUserUnreadPosts({
            authorUserId: payload.authorUserId,
            after: payload.lastId,
            limit: 15,
          });
          posts = response;
          break;
        default:
          throw new Error("Unknown tabId");
      }

      if (posts.statusCode) {
        authErrorHandler(posts);
      }
      return posts;
    } catch (error) {
      console.error("Error in setNextPagonationPosts:", error);
      throw new Error("Failed to fetch posts. Please try again later.");
    }
  },

  /** Fetch suggested-users-to-follow and commit them (always as an array, even when empty). */
  async setSuggestedPosts({ commit }) {
    const params = new URLSearchParams({
      limit: "50", // Request more users to account for frontend filtering
    }).toString();
    try {
      let response = await fetch(`/api/users/suggested?${params}`, {
        headers: {
          "Dougjones": "web",
        },
        credentials: "include",
        cache: "no-store",
      });

      if (!response.ok) {
        console.error("Network response was not ok:", response.statusText);
        return;
      }

      let posts = await response.json();

      if (posts.statusCode) {
        authErrorHandler(posts);
        return;
      }

      if (posts && posts.length > 0) {
        commit("setPostsSuggested", posts);
      } else {
        // Ensure state is always an array to avoid template checks failing
        commit("setPostsSuggested", []);
        if (import.meta.env.MODE === "development") {
          console.warn("No suggested posts found.");
        }
      }
    } catch (error) {
      console.error("Failed to fetch suggested posts:", error);
      commit("setPostsSuggested", []);
    }
  },

  /** Load a single post by id into `state.post` for the post-detail view. */
  async setRecommendedPost({ commit }, payload) {
    // FIX: guard against undefined/invalid payload to prevent /api/posts/undefined 400 error
    // Check both JS undefined and the string "undefined" which comes from route.params.id
    // when navigating to /post/undefined
    if (!payload || payload === 'undefined' || payload === 'null') return;
    commit("setPost", null);
    let response = await fetch(`/api/posts/${payload}`);
    let posts = await response.json();
    if (posts.statusCode) {
      authErrorHandler(posts);
    }
    commit("setPost", posts);
  },

  /**
   * Load the Following/For You feed. Warm-starts from cache, fetches the page(s)
   * (on the `dougjoneslight` theme preset, a welcome-post page is fetched and merged
   * with the regular page), commits the base results, then hydrates replies in the
   * background. Bails out (without committing) if the session was invalidated mid-fetch.
   */
  async setForYouPosts({ commit, rootState }, payload) {
    // Debug log removed
    const params = new URLSearchParams();
    // params.set("limit", "30");
    params.set("limit", "10");
    // Show the current user's own posts in filtered views (parity with recommended feed)
    params.set("showOwnPosts", "true");
    const effectiveLabel = payload?.label || payload?.data?.label; // support both shapes
    if (
      effectiveLabel &&
      effectiveLabel !== "All" &&
      effectiveLabel !== "Following" &&
      effectiveLabel !== "Monitoring"
    ) {
      params.set("tag", toApiTag(effectiveLabel));
    }
    // NOTE: Previously we set only=positive for 'Following'. This likely filtered out many posts.
    // Remove that restriction so we actually see all followed content. Keep Monitoring (negative) logic below.
    if (effectiveLabel === "Monitoring") {
      params.set("only", "negative");
    }
    let postType;
    // Default Following feed should not include standalone replies; they are integrated via enhancePostsWithReplies
    if (payload?.postsType?.type !== "Reply") {
      postType = "&type=post&type=quote&type=repost";
    } else {
      postType = "&type=reply";
    }

    // 0) Instant warm start via cache
    const cacheKey = `feed:foryou:${effectiveLabel || "All"}:${postType.includes("reply") ? "replies" : "posts"}`;
    const cached = readFeedCache(cacheKey);
    if (cached && cached.length) {
      commit("setFollowingPosts", { payload: cached, postType });
    }

    // Fetches one for-you page and normalizes the response shape.
    // Returns loggedOut:true if the session was invalidated so the caller can bail out.
    const fetchForYouPage = async (extraParams) => {
      const response = await fetch(
        `/api/posts/for-you?${params.toString()}${postType}${extraParams}`
      );
      // FIX: parse body once before checking status — previous code called response.json() twice
      // (once in the 401/403 check, once below), causing "body already consumed" error that
      // silently returned [] and never committed posts to the store.
      let posts;
      try {
        posts = await response.json();
      } catch (e) {
        return { results: [] };
      }
      if (response.status === 403 || response.status === 401) {
        if (
          posts?.message === "User account is deleted" ||
          posts?.message === "User no longer exists" ||
          posts?.message === "Unauthorized"
        ) {
          // Don't navigate away immediately — show the session-expired popup
          // and let the user trigger logout/redirect via its Log In button.
          triggerSessionExpired();
          return { results: [], loggedOut: true };
        }
      }
      if (posts.statusCode) {
        authErrorHandler(posts);
      }
      // Normalize possible response shapes:
      // - { results: [...] }
      // - [...]
      // - { data: [...] }
      const results = Array.isArray(posts?.results)
        ? posts.results
        : Array.isArray(posts)
          ? posts
          : Array.isArray(posts?.data)
            ? posts.data
            : [];
      return { results };
    };

    // The welcome-post page + regular page merge is specific to the dougjoneslight theme preset.
    const isDougJonesLight = import.meta.env.VITE_THEME_PRESET === "dougjoneslight";

    let rawResults;
    if (isDougJonesLight) {
      // 1) Welcome post page first, then the regular for-you page — append both into one list.
      const welcomePage = await fetchForYouPage("&isWelcomePost=true");
      if (welcomePage.loggedOut) return;
      const regularPage = await fetchForYouPage("");
      if (regularPage.loggedOut) return;

      // Tag posts from the welcome-post page so the UI can restrict them to Like & Comment only.
      welcomePage.results.forEach((p) => {
        if (p) p.isWelcomePost = true;
      });

      // Dedupe by id in case a post shows up in both pages.
      const seenIds = new Set();
      rawResults = [...welcomePage.results, ...regularPage.results].filter((p) => {
        const id = p?.id || p?._id;
        if (id == null) return true;
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
      });
    } else {
      const regularPage = await fetchForYouPage("");
      if (regularPage.loggedOut) return;
      rawResults = regularPage.results;
    }

    // Defensive: if API changed and returns nested object
    const baseResultsFiltered = rawResults.filter(
      (p) => !(p?.type === "reply" && p?.repostedPost?.type === "reply")
    );
    const baseResults =
      baseResultsFiltered.length > 0 || rawResults.length === 0
        ? baseResultsFiltered
        : rawResults;

    // Commit even if empty so UI can show empty state (previously we could silently skip)
    // Debug log removed
    commit("setFollowingPosts", { payload: baseResults, postType });
    writeFeedCache(cacheKey, baseResults);

    if (baseResults.length > 0) {
      enhancePostsWithReplies(baseResults, { concurrency: 6 })
        .then(async (enhanced) => {
          // Re-check length to avoid overwriting with empty due to enhancement edge cases
          if (Array.isArray(enhanced) && enhanced.length) {
            await nextTick();
            commit("setFollowingPosts", { payload: enhanced, postType });
            writeFeedCache(cacheKey, enhanced);
          }
        })
        .catch((e) =>
          console.debug(
            "enhancePostsWithReplies failed (following):",
            e?.message
          )
        );
    }

    // Debug log removed

    return baseResults;
  },

  /** Fetch posts for a given user (`payload.id`) or the default feed if no id is given. */
  async setUserPosts({ commit }, payload) {
    let params = new URLSearchParams({ limit: "10" }).toString();
    if (payload?.id) {
      params = new URLSearchParams({
        limit: "10",
        authorUserId: payload?.id,
      }).toString();
    }
    let response = await fetch(`/api/posts?${params}`, {
      method: "POST",
      headers: {
        "Dougjones": "web",
      },
    });
    let posts = await response.json();
    if (posts.statusCode) {
      authErrorHandler(posts);
    }
    commit("setPosts", posts.results);
  },
  /** Fetch a user's profile by id and commit it, returning the profile data. */
  async setProfile({ commit }, id) {
    let response = await fetch(`/api/users/${id}`);
    let profile = await response.json();
    if (profile.statusCode) {
      authErrorHandler(profile);
    }
    commit("setProfile", profile);
    return profile;
  },
  /** Load replies for a post (`payload` = post id), merged with synthesized reaction comments, into `state.replies`. */
  async setRepliesPosts({ commit }, payload) {
    // FIX: guard against undefined/null payload to prevent /api/posts/undefined requests
    // Checks JS undefined AND string "undefined" (from route.params.id when navigating to /post/undefined)
    /* OLD: // if(payload === "null") return; */
    if (!payload || payload === 'null' || payload === 'undefined') return;
    commit("setRepliePost", []);
    let response = await fetch(`/api/posts/${payload}/replies`);
    let posts = await response.json();
    if (posts.statusCode) {
      authErrorHandler(posts);
    }
    // Stopgap: also include existing reaction comments (with description) as replies
    try {
      const postRes = await fetch(`/api/posts/${payload}`);
      const postObj = await postRes.json();
      const serverReplies = Array.isArray(posts?.results)
        ? posts.results.slice().reverse()
        : [];
      const synthetic = await synthesizeReactionCommentsFromPost(postObj, {
        max: 10,
      });
      // Dedup by author+normalized text (ignore date)
      const normalize = (t) =>
        (t || "").toString().toLowerCase().replace(/\s+/g, " ").trim();
      const seen = new Set(
        serverReplies.map((r) => `${r.authorUserId}|${normalize(r.text)}`)
      );
      const merged = [...serverReplies];
      for (const s of synthetic) {
        const key = `${s.authorUserId}|${normalize(s.text)}`;
        if (!seen.has(key)) {
          merged.push(s);
          seen.add(key);
        }
      }
      commit("setRepliePost", merged);
    } catch (_) {
      commit("setRepliePost", posts?.results?.reverse());
    }
  },

  // Add optimistic reply for immediate UI feedback
  addOptimisticReply({ commit }, replyData) {
    commit("addOptimisticReply", replyData);
  },

  // Remove optimistic reply
  removeOptimisticReply({ commit }, tempId) {
    commit("removeOptimisticReply", tempId);
  },
  /** Toggle the generic `loadStatus` loading flag. */
  async setUpdateStatus({ commit }, payload) {
    commit("setStatusLoad", payload);
  },
  /** Create a new post/reply/quote via the API, handling rate-limit/error toasts and store updates on success. */
  async setPost(
    { commit },
    {
      title,
      rawText,
      text,
      repostedPostId,
      attachedFileIds,
      isAnyoneCanReply,
      postToTwitter,
      postToBluesky,
      weighInThresholds,
      urls,
      mentions,
      channelId,
      displayedChannelId,
      isMemberOrg,
      notifyGroupMembers,
      tags,
      toast,
      isReply,
      isComment,
    }
  ) {
    try {
      let response = await fetch(`/api/posts`, {
        method: "POST",
        body: JSON.stringify({
          title: title ? title : undefined,
          rawText: rawText ? rawText : undefined,
          text,
          repostedPostId: repostedPostId ? repostedPostId : undefined,
          attachedFileIds: attachedFileIds ? attachedFileIds : undefined,
          isAnyoneCanReply: isAnyoneCanReply,
          channelId: channelId,
          displayedChannelId: displayedChannelId
            ? displayedChannelId
            : undefined,
          postToTwitter: postToTwitter,
          mentions: mentions ? mentions : undefined,
          postToBluesky: postToBluesky,
          ...(isMemberOrg ? { notifyGroupMembers } : {}),
          // Send all URLs — component passes allLinksOG (full array) as urls.
          // Previously only urls[0] was sent; reverted back then re-applied after
          // createPostModal.vue was updated to populate allLinksOG for every URL.
          urls: Array.isArray(urls) && urls.length
            ? urls.map((u) => ({
                url: u.url,
                title: u.title,
                description: u.description,
                imageUrl: u.imageUrl,
                isDisabledPreview: u.isDisabledPreview,
              }))
            : undefined,
          ...(isComment === false ? { isComment: false } : {}),
          tags: tags ? tags : undefined,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.status === 401 || response.status === 403) {
        authErrorHandler(response);
      }
      if (response.status === 429) {
        toast.add({
          severity: "error",
          summary:
            "Sorry, you have reached the max number of posts allowed for users with your score",
          life: 7000,
        });
        return response;
      }
      if (!response.ok) {
        const errorBody = await response.json();
        toast.add({
          severity: "error",
          summary: errorBody?.message || "Request failed",
          life: 7000,
        });
        return errorBody;
      }

      const data = await response.json();

      // GA4: post_type normalized from the same isReply/isComment flags the
      // request payload itself is built from — see Post Events section of
      // docs/GOOGLE_ANALYTICS_TRACKING_PLAN.md.
      const postTypeForAnalytics = isReply
        ? "reply"
        : isComment === false
          ? "quote"
          : "post";

      if (data.error) {
        // console.log("error", data.message);
      } else {
        if (response.ok) {
          if (isMemberOrg) {
            commit("addOrgPost", data, { root: true });

            toast.add({
              severity: "success",
              summary: isReply ? "Reply created" : "Post created",
              life: 7000,
            });

            trackAnalyticsEvent("post_create_success", {
              post_id: data.id,
              post_type: postTypeForAnalytics,
              channel_id: channelId,
              group_id: channelId,
              has_media: Array.isArray(attachedFileIds) && attachedFileIds.length > 0,
              media_count: attachedFileIds?.length || 0,
              has_links: Array.isArray(urls) && urls.length > 0,
              link_count: urls?.length || 0,
              has_mentions: Array.isArray(mentions) && mentions.length > 0,
              mention_count: mentions?.length || 0,
              has_tags: Array.isArray(tags) && tags.length > 0,
              tag_count: tags?.length || 0,
              posted_as: "group",
              brand: analyticsBrand(),
            });

            return response;
          }

          const payload = { data: data, type: "create" };
          localStorage.removeItem("thresholdsPost");
          commit("postPost", payload);

          if (weighInThresholds) {
            let resp = await fetch(
              `/api/posts/${data.id}/weigh-in-thresholds`,
              {
                method: "POST",
                body: JSON.stringify({
                  thresholds: weighInThresholds ? weighInThresholds : undefined,
                }),
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
          }
          toast.add({
            severity: "success",
            summary: isReply ? "Reply created" : "Post created",
            life: 7000,
          });

          // emit event for add replyList tree
          if (isReply) {
            eventBus.emit("add-reply", { parentId: data.id, newReply: data });
          }

          trackAnalyticsEvent("post_create_success", {
            post_id: data.id,
            post_type: postTypeForAnalytics,
            channel_id: channelId,
            group_id: isMemberOrg ? channelId : undefined,
            has_media: Array.isArray(attachedFileIds) && attachedFileIds.length > 0,
            media_count: attachedFileIds?.length || 0,
            has_links: Array.isArray(urls) && urls.length > 0,
            link_count: urls?.length || 0,
            has_mentions: Array.isArray(mentions) && mentions.length > 0,
            mention_count: mentions?.length || 0,
            has_tags: Array.isArray(tags) && tags.length > 0,
            tag_count: tags?.length || 0,
            posted_as: isMemberOrg ? "group" : "user",
            brand: analyticsBrand(),
          });

          return response;
        } else {
          commit("setUserDataError", data);
        }
      }
    } catch (error) {
      // console.log("error", error);
      commit("setUserDataError", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error,
        life: 7000,
      });
    }
  },
  /**
   * Publish a single post to multiple profiles/groups at once via the seed-content batch
   * endpoint. Same payload/shape as setPost — the only addition is `channelIds`, the list of
   * every target channel — so the backend can fan the post out itself instead of the caller
   * looping one /api/posts request per selected profile.
   */
  async setSeedContentPost(
    { commit },
    {
      title,
      rawText,
      text,
      repostedPostId,
      attachedFileIds,
      isAnyoneCanReply,
      postToTwitter,
      postToBluesky,
      weighInThresholds,
      urls,
      mentions,
      channelId,
      channelIds,
      displayedChannelId,
      isMemberOrg,
      notifyGroupMembers,
      tags,
      toast,
      isReply,
      isComment,
    }
  ) {
    try {
      let response = await fetch(`/api/posts/seed-content`, {
        method: "POST",
        body: JSON.stringify({
          title: title ? title : undefined,
          rawText: rawText ? rawText : undefined,
          text,
          repostedPostId: repostedPostId ? repostedPostId : undefined,
          attachedFileIds: attachedFileIds ? attachedFileIds : undefined,
          isAnyoneCanReply: isAnyoneCanReply,
          channelId: channelId,
          channelIds: channelIds,
          displayedChannelId: displayedChannelId
            ? displayedChannelId
            : undefined,
          postToTwitter: postToTwitter,
          mentions: mentions ? mentions : undefined,
          postToBluesky: postToBluesky,
          ...(isMemberOrg ? { notifyGroupMembers } : {}),
          urls: Array.isArray(urls) && urls.length
            ? urls.map((u) => ({
                url: u.url,
                title: u.title,
                description: u.description,
                imageUrl: u.imageUrl,
                isDisabledPreview: u.isDisabledPreview,
              }))
            : undefined,
          ...(isComment === false ? { isComment: false } : {}),
          tags: tags ? tags : undefined,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.status === 401 || response.status === 403) {
        authErrorHandler(response);
      }
      if (response.status === 429) {
        toast.add({
          severity: "error",
          summary:
            "Sorry, you have reached the max number of posts allowed for users with your score",
          life: 7000,
        });
        return response;
      }
      if (!response.ok) {
        const errorBody = await response.json();
        toast.add({
          severity: "error",
          summary: errorBody?.message || "Request failed",
          life: 7000,
        });
        return errorBody;
      }

      const data = await response.json();

      trackAnalyticsEvent("post_create_success", {
        post_id: data?.id,
        post_type: isReply ? "reply" : isComment === false ? "quote" : "post",
        channel_id: channelId,
        channel_ids: channelIds,
        has_media: Array.isArray(attachedFileIds) && attachedFileIds.length > 0,
        media_count: attachedFileIds?.length || 0,
        has_links: Array.isArray(urls) && urls.length > 0,
        link_count: urls?.length || 0,
        has_mentions: Array.isArray(mentions) && mentions.length > 0,
        mention_count: mentions?.length || 0,
        has_tags: Array.isArray(tags) && tags.length > 0,
        tag_count: tags?.length || 0,
        posted_as: "multi-profile",
        brand: analyticsBrand(),
      });

      toast.add({
        severity: "success",
        summary: isReply ? "Reply created" : "Post created",
        life: 7000,
      });

      return response;
    } catch (error) {
      commit("setUserDataError", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error,
        life: 7000,
      });
    }
  },
  /** Patch an existing post's editable fields (title/text/attachments/tags/urls/reply setting). */
  async updatePost(
    { commit },
    { title, text, attachedFileIds, isAnyoneCanReply, id, urls, tags, toast }
  ) {
    try {
      // Allow minimal tags-only payloads for classification updates
      const base = {};
      if (typeof title !== "undefined") base.title = title;
      if (typeof text !== "undefined") base.text = text;
      if (typeof attachedFileIds !== "undefined")
        base.attachedFileIds = attachedFileIds;
      if (typeof isAnyoneCanReply !== "undefined")
        base.isAnyoneCanReply = isAnyoneCanReply;
      if (Array.isArray(tags)) base.tags = tags;
      // Send all URLs — same multi-URL fix as setPost above.
      if (Array.isArray(urls) && urls.length) {
        base.urls = urls.map((u) => ({
          url: u.url,
          title: u.title,
          description: u.description,
          imageUrl: u.imageUrl,
          isDisabledPreview: u.isDisabledPreview,
        }));
      }
      const payload = base;

      const response = await fetch(`/api/posts/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.status) {
        authErrorHandler(response);
      }
      if (response.status === 429) {
        toast.add({
          severity: "error",
          summary:
            "Sorry, you have reached the max number of posts allowed for users with your score",
          life: 7000,
        });
        return;
      }

      const data = await response.json();

      if (
        data.statusCode === 409 ||
        data.message ===
          "A post may not be edited after it has been responded or reacted to"
      ) {
        toast.add({
          severity: "error",
          summary: "Can't edit post",
          detail: data.message,
          life: 7000,
        });
        return;
      }

      if (data.error) {
        // console.log("error", data.message);
      } else {
        const payload = { data: data, type: "update" };
        commit("postPost", payload);
        commit("setPost", data);
        // postPost only patches this (home) module's own feed collections —
        // orgPosts lives in the separate group module, so a group post/reply
        // edited here would otherwise never update in org.vue's/channel.vue's
        // list until a full refetch. updateOrgPost no-ops if the post isn't there.
        commit("updateOrgPost", data);
        // emit event for add replyList tree
        if (data.type === "reply") {
          eventBus.emit("add-reply", { parentId: data.id, newReply: data });
        }

        if (!data.statusCode) {
          toast.add({
            severity: "success",
            summary: "Post updated",
            life: 7000,
          });
        } else {
          commit("setUserDataError", data);
        }
      }
    } catch (error) {
      // console.log("error", error);
      commit("setUserDataError", error);
    }
  },
  /** Delete a post and remove it from local state. Returns `{ ok: true|false }` so callers can revert optimistic UI on failure. */
  async deletePost({ commit }, { postId, toast, type }) {
    let data = null;
    // OLD: this action never returned anything — every caller awaiting it
    // got `undefined` whether the delete succeeded or failed (the catch
    // below logs but never rethrows), so callers had no way to tell the two
    // apart and react (e.g. reverting an optimistic UI removal on failure).
    try {
      const response = await fetch(`/api/posts/${postId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      let data = {};
      try { data = await response.json(); } catch (_) {}

      const succeeded = response.ok && data?.status_id !== 0;

      if (succeeded) {
        const typePost = type ? type.charAt(0).toUpperCase() + type.slice(1) : "Post";
        if (toast) {
          toast.add({
            severity: "success",
            summary: `${typePost} deleted`,
            life: 3000,
          });
        }
        commit("removePost", postId);

        // if (type === "reply") {
        //   //commit("setBlockOnReplies", [postId]);
        // } else {

        // }
        return { ok: true };
      } else {
        if (toast) {
          toast.add({
            severity: "error",
            summary: data?.reason || data?.message || "Failed to delete post",
            life: 7000,
          });
        }
        throw new Error(data?.reason || data?.message || "Failed to delete the post");
      }
    } catch (error) {
      console.error("Error in deletePost action:", error.message);
      return { ok: false };
    }
  },
  /** Start a 60s interval that advances `state.now`, driving relative-time UI updates. */
  startGlobalTimer({ commit }) {
    setInterval(() => {
      commit("updateNow");
    }, 60000);
  },
  /** Load a channel's reply posts, enriched with reaction comments synthesized from that user's own posts, sorted newest first. */
  async setUserReplies({ commit }, channelId) {
    // const limit = 20;
    const limit = 10;
    const response = await fetch(
      `/api/posts?channelId=${channelId}&limit=${limit}&type=reply`,
      { credentials: "include" }
    );
    if (response.status === 403 || response.status === 401) {
      const data = await response.json().catch(() => ({}));
      if (
        data?.message === "User account is deleted" ||
        data?.message === "User no longer exists" ||
        data?.message === "Unauthorized"
      ) {
        triggerSessionExpired();
        return;
      }
    }
    const ct = response.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const text = await response.text().catch(() => "");
      console.error("[setUserReplies] Non-JSON response", {
        status: response.status,
        snippet: text.slice(0, 200),
      });
      return { results: [], count: 0 };
    }
    let replies = await response.json();
    if (replies.statusCode) {
      authErrorHandler(replies);
    }

    // Enhanced: include reaction comments from the user's own posts
    try {
      const serverReplies = Array.isArray(replies?.results)
        ? replies.results.slice()
        : [];

      // Fetch user's own posts to get reaction comments
      const userPostsResponse = await fetch(
        `/api/posts?channelId=${channelId}&limit=50&type=post`,
        { credentials: "include" }
      );
      if (userPostsResponse.ok) {
        const userPostsData = await userPostsResponse.json();
        const userPosts = Array.isArray(userPostsData?.results)
          ? userPostsData.results
          : [];

        // Synthesize reaction comments from all user's posts
        const allReactionComments = [];
        for (const post of userPosts) {
          const synthetic = await synthesizeReactionCommentsFromPost(post, {
            max: 10,
          });
          allReactionComments.push(...synthetic);
        }

        // Merge server replies with reaction comments and deduplicate
        const normalize = (t) =>
          (t || "").toString().toLowerCase().replace(/\s+/g, " ").trim();
        const seen = new Set(
          serverReplies.map((r) => `${r.authorUserId}|${normalize(r.text)}`)
        );
        const merged = [...serverReplies];

        for (const s of allReactionComments) {
          const key = `${s.authorUserId}|${normalize(s.text)}`;
          if (!seen.has(key)) {
            merged.push(s);
            seen.add(key);
          }
        }

        // Sort by creation date (newest first)
        merged.sort((a, b) => {
          const bt = new Date(b.createdAt || b.publishedAt).getTime();
          const at = new Date(a.createdAt || a.publishedAt).getTime();
          return bt - at;
        });

        commit("setUserReplies", merged);
        commit("setTotalCountPosts", merged.length);
        return { results: merged, count: merged.length };
      }
    } catch (error) {
      console.error(
        "[setUserReplies] Error synthesizing reaction comments:",
        error
      );
    }

    // Fallback to original behavior if enhanced processing fails
    if (replies.count > 0) {
      commit("setUserReplies", replies.results);
      commit("setTotalCountPosts", replies.count);
    }
    return replies;
  },
  /** Load a channel's repost posts into `state.userReposts`. */
  async setUserReposts({ commit }, channelId) {
    const limit = 10;
    const response = await fetch(
      `/api/posts?channelId=${channelId}&limit=${limit}&type=repost`,
      { credentials: "include" }
    );
    if (response.status === 403 || response.status === 401) {
      const data = await response.json().catch(() => ({}));
      if (
        data?.message === "User account is deleted" ||
        data?.message === "User no longer exists" ||
        data?.message === "Unauthorized"
      ) {
        triggerSessionExpired();
        return;
      }
    }
    const ct2 = response.headers.get("content-type") || "";
    if (!ct2.includes("application/json")) {
      const text = await response.text().catch(() => "");
      console.error("[setUserReposts] Non-JSON response", {
        status: response.status,
        snippet: text.slice(0, 200),
      });
      return { results: [], count: 0 };
    }
    let reposts = await response.json();
    if (reposts.statusCode) {
      authErrorHandler(reposts);
    }
    if (reposts.count > 0) {
      commit("setUserReposts", reposts.results);
      commit("setTotalCountPosts", reposts.count);
    }
    return reposts;
  },

  /** Submit a reaction (with optional comment) to a post; shows success/duplicate/error toasts and returns the API response. */
  async setReactionPost({ commit }, payload) {
    try {
      const { toast } = payload;
      const response = await fetch(`/api/posts/${payload.id}/react`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Dougjones": "web",
        },
        body: JSON.stringify({
          values: payload.values, // array of { categoryId, value }
          description: payload.description || null,
        }),
      });

      const responseData = await response.json();
      if (!response.ok || responseData.statusCode) {
        if (responseData.statusCode === 409) {
          // User already reacted
          toast.add({
            severity: "warn",
            summary: "You already reacted to this post",
            life: 4000,
          });
        } else {
          toast.add({
            severity: "error",
            summary: responseData.message || "Failed to react to post",
            life: 4000,
          });
        }
        authErrorHandler(responseData);
        throw new Error(responseData.message || "Failed to react to post");
      }

      toast.add({
        severity: "success",
        summary: "Reaction submitted successfully",
        life: 3000,
      });


      return responseData;
    } catch (error) {
      console.error("Error reacting to post:", error);
      throw error;
    }
  },

  /** Create or undo a repost of `payload.id`, depending on whether `payload.repostedPostId` is already set. */
  async setToggleRepost({ commit }, payload) {
    const response = await fetch(
      payload.repostedPostId
        ? `/api/posts/${payload.id}/undo-repost`
        : `/api/posts/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          repostedPostId: payload.repostedPostId ? undefined : payload.id,
          channelId: payload.channelId || undefined,
        }),
      }
    );
    let res = await response.json();
    if (res.statusCode) {
      authErrorHandler(res);
    }
    commit("removePost", res[0]);
    return res;
  },

  /** Load a user/channel's own posts (post/quote/repost types) for the profile "Posts" tab. */
  async setOnlyUserPosts({ commit }, { id, limit, channel }) {
    const params = new URLSearchParams({
      limit: String(limit || "10"),
      channelId: String(channel ? channel.id : id),
      onlyPosts: String(true),
    }).toString();
    // OLD: no explicit `type` filter — the feed endpoints that correctly
    // show quote posts (setForYouPosts/setRecommendedPosts) explicitly
    // request type=post/quote/repost; "onlyPosts=true" alone appears to
    // default to plain posts only server-side, silently excluding quotes
    // (and reposts) from the profile's own Posts tab even though the same
    // post shows up fine elsewhere.
    // let response = await fetch(`/api/posts?${params}`, {
    //   credentials: "include",
    // });
    const typeFilter = "&type=post&type=quote&type=repost";
    let response = await fetch(`/api/posts?${params}${typeFilter}`, {
      credentials: "include",
    });
    const ct = response.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const text = await response.text().catch(() => "");
      console.error("[setOnlyUserPosts] Non-JSON response", {
        status: response.status,
        snippet: text.slice(0, 200),
      });
      return { results: [], count: 0 };
    }
    let posts = await response.json();
    if (posts.statusCode) {
      authErrorHandler(posts);
    }
    if (posts.count > 0) {
      commit("setOnlyUserPosts", posts.results);
      commit("setTotalCountPosts", posts.count);
    }
    return posts;
  },

  /** Fetch the list of reputation/reaction categories used by the reaction picker. */
  async fetchReputationCategories({ commit }) {
    const { data, status, error, refresh } = await useAPI(
      `/users/reputation-categories`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    commit("setReputationCategories", data);
  },
  /** Set the post id currently targeted by the report-post flow. */
  async fetchReportBotId({ commit }, id) {
    commit("setReportBotId", id);
  },

  /**
   * Fetch a user's profile by username for the profile view. If the profile
   * belongs to the logged-in user, also refreshes the cached `auth.user`
   * snapshot so the rest of the app isn't left showing stale login-time data.
   */
  async fetchUsernameData({ commit, rootState }, username) {
    try {
      const response = await fetch(`/api/users/username/${username}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (response.status === 403 || response.status === 401) {
        const data = await response.json().catch(() => ({}));
        if (
          data?.message === "User account is deleted" ||
          data?.message === "User no longer exists" ||
          data?.message === "Unauthorized"
        ) {
          triggerSessionExpired();
          return;
        }
      }
      if (response.status) {
        authErrorHandler(response);
      }
      if (response.ok) {
        const contentType = response.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          const text = await response.text().catch(() => "");
          console.error("[fetchUsernameData] Non-JSON response", {
            status: response.status,
            snippet: text?.slice(0, 200),
          });
          throw new Error("Non-JSON response when fetching username data");
        }
        const data = await response.json();
        commit("setUsernameData", data);

        // If this is the logged-in user's own profile, this fresher fetch
        // (triggered by visiting /user/:username) should also refresh the
        // cached "me" data set at login (auth.user) — otherwise the rest of
        // the app keeps showing the stale login-time snapshot until the next
        // explicit getMe() call.
        if (data?.id && data.id === rootState?.auth?.user?.id) {
          commit("setUserData", data);
          localStorage.setItem("user", JSON.stringify(data));
        }
      } else {
        const text = await response.text().catch(() => "");
        console.error("[fetchUsernameData] Non-OK response", {
          status: response.status,
          snippet: text?.slice(0, 200),
        });
        throw new Error("Failed to fetch username");
      }
    } catch (error) {
      console.error("Error:", error.message);
    }
  },

  // NEW: id-based counterpart of fetchUsernameData — used by /profile/:id routes
  // (e.g. short-link redirects), which previously never populated usernameData
  // and left the profile page on "User not found or unable to load profile data."
  async fetchUserDataById({ commit, rootState }, id) {
    try {
      const response = await fetch(`/api/users/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        console.error("[fetchUserDataById] Non-OK response", {
          status: response.status,
          snippet: text?.slice(0, 200),
        });
        throw new Error("Failed to fetch user by id");
      }
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error("Non-JSON response when fetching user by id");
      }
      const data = await response.json();
      commit("setUsernameData", data);
      // Same own-profile refresh as fetchUsernameData
      if (data?.id && data.id === rootState?.auth?.user?.id) {
        commit("setUserData", data);
        localStorage.setItem("user", JSON.stringify(data));
      }
      return data;
    } catch (error) {
      console.error("Error:", error.message);
      return null;
    }
  },

  /** Patch a post's blocked-repliers list (`payload.blocked`) so specific users can't reply to it. */
  async blockUserOnReplies({ commit }, payload) {
    try {
      const { id, blocked, toast } = payload;

      const response = await fetch(`/api/posts/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(blocked),
      });

      if (!response.ok) {
        console.error(
          `Failed to update post with ID ${id}: ${response.statusText}`
        );
      } else {
        const data = await response.json();
        // commit("setBlockOnReplies", blocked.blockedUsersIds);
        toast.add({
          severity: "success",
          summary: "Blocked!",
          detail: "The user has been blocked from replying to this post.",
          life: 3000,
        });
        return data;
      }
    } catch (error) {
      console.error("Error updating post:", error);
      throw error;
    }
  },

  /** Block or unblock a user (`payload.actionType`: 'block'|'unblock') and show a confirmation toast. */
  async blockUnblockUser({ commit }, payload) {
    try {
      const { id, actionType, toast } = payload;
      const response = await fetch(`/api/users/${id}/${actionType}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.error(
          `Failed to ${actionType} user with ID ${id}: ${response.statusText}`
        );
      } else {
        const data = await response.json();
        //commit("setBlockOnReplies", blocked.blockedUsersIds);
        const message =
          actionType === "block"
            ? "You've successfully blocked this user. Their posts and interactions are now hidden."
            : "You can now view and interact with this user's posts again.";
        toast.add({
          severity: "success",
          summary: `User ${actionType}ed`,
          detail: message,
          life: 3000,
        });
        return data;
      }
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  },

  /** Create a shareable short link for post `id`. */
  async createShortLink({ commit }, id) {
    let response = await fetch(`/api/links`, {
      method: "POST",
      body: JSON.stringify({
        postId: id,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    let data = await response.json();
    return data;
  },

  /** Resolve a short-link code to its link record, then record a visit against it. */
  async checkVisitLink({ commit }, name) {
    // Step 1: resolve short code / name → actual link record with database id
    const linkRes = await fetch(`/api/links/${name}`);
    const link = await linkRes.json();
    // Step 2: visit using the actual database id
    let response = await fetch(`/api/links/${link.id}/visit`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
    let data = await response.json();
    return data;
  },

  /** Set whether the compose box is currently in "reply" mode. */
  setIsReply({ commit }, isReply) {
    commit("setIsReply", isReply);
  },

  /** Remove a reported post from all feed collections. */
  removeReportedPost({ commit }, postId) {
    commit("removeReportedPost", postId);
  },

  /** Remove a reported post from one specific named collection. */
  removeReportedPostWithCollection({ commit }, { postId, collectionName }) {
    commit("removeReportedPostWithCollection", { postId, collectionName });
  },

  /** Replace a reply in `state.replies` with an updated version (e.g. after reacting). */
  setReaction({ commit }, post) {
    commit("setReaction", post);
  },

  /** Set the reply-post-in-progress status flag. */
  setUpdateStatusReply({ commit }, status) {
    commit("addReplyPostStatus", status);
  },

  /** Merge a page of fetched data into a named collection, deduped by post id. */
  addCollection({ commit }, { collectionName, data, isReply }) {
    commit("setCollection", { collectionName, data, isReply });
  },

  /**
   * Search users, posts, and groups for `query`. A leading "@" searches users/groups
   * by username; otherwise runs a combined search across users, posts, and both
   * "my" and "other" organizations. Falls back to a username search if the plain
   * query finds no users. Clears results for queries under 2 characters.
   */
  async fetchSearchResults({ commit }, query) {
    if (query.trim().length < 2) {
      commit("clearSearchResults");
      commit("setNoSearchResults", null);
      return;
    }

    try {
      let users = [];
      let posts = [];
      let groups = [];
      let originalQuery = query;
      // 1. Try normal search
      if (query.startsWith("@")) {
        const cleanQuery = query.substring(1);
        const userParams = new URLSearchParams({
          search: cleanQuery,
          autocomplete: "true",
        });
        const userResponse = await fetch(`/api/users?${userParams}`);
        const userData = await userResponse.json();
        users = Array.isArray(userData) ? userData : userData.results || [];
        // Fetch groups (both other + my) matching same cleanQuery
        try {
          const [myGroupsResp, otherGroupsResp] = await Promise.all([
            fetch(
              `/api/organizations/my?search=${encodeURIComponent(cleanQuery)}&limit=50&autocomplete=true`
            ),
            fetch(
              `/api/organizations/other?search=${encodeURIComponent(cleanQuery)}&limit=50&autocomplete=true`
            ),
          ]);
          const myGroupsData = await myGroupsResp.json();
          const otherGroupsData = await otherGroupsResp.json();
          const myGroups = Array.isArray(myGroupsData)
            ? myGroupsData
            : myGroupsData.results || [];
          const otherGroups = Array.isArray(otherGroupsData)
            ? otherGroupsData
            : otherGroupsData.results || [];
          const seenGroupIds = new Set();
          groups = [...myGroups, ...otherGroups].filter((g) => {
            if (!g || !g.id) return false;
            if (seenGroupIds.has(g.id)) return false;
            seenGroupIds.add(g.id);
            return true;
          });
        } catch (e) {
          if (import.meta.env.MODE === "development")
            console.warn('Group search ("@" branch) failed', e);
        }
      } else {
        const [userResponse, postResponse, groupResponse, myGroupResponse] =
          await Promise.all([
            fetch(`/api/users?search=${encodeURIComponent(query)}`),
            fetch(`/api/search?query=${encodeURIComponent(query)}`),
            fetch(
              `/api/organizations/other?search=${encodeURIComponent(query)}&limit=50`
            ),
            fetch(
              `/api/organizations/my?search=${encodeURIComponent(query)}&limit=50`
            ),
          ]);
        const userData = await userResponse.json();
        const postData = await postResponse.json();
        const groupData = await groupResponse.json();
        const myGroupData = await myGroupResponse.json();
        users = Array.isArray(userData) ? userData : userData.results || [];
        posts = Array.isArray(postData)
          ? postData
              .filter((item) => item.collection === "posts")
              .map((item) => ({ ...item.render, id: item.render.id || item.render._id }))
          : [];
        const otherGroups = Array.isArray(groupData)
          ? groupData
          : groupData.results || [];
        const myGroups = Array.isArray(myGroupData)
          ? myGroupData
          : myGroupData.results || [];
        const seenIds = new Set();
        groups = [...myGroups, ...otherGroups].filter((g) => {
          if (!g || !g.id) return false;
          if (seenIds.has(g.id)) return false;
          seenIds.add(g.id);
          return true;
        });
      }
      // 2. If nothing is found, try with @ (for search by username)
      if (!users.length && !query.startsWith("@")) {
        const cleanQuery = query;
        const userParams = new URLSearchParams({
          search: cleanQuery,
          autocomplete: "true",
        });
        const userResponse = await fetch(`/api/users?${userParams}`);
        const userData = await userResponse.json();
        users = Array.isArray(userData) ? userData : userData.results || [];
        // Try groups on fallback as well if still empty
        try {
          const [myGroupsResp, otherGroupsResp] = await Promise.all([
            fetch(
              `/api/organizations/my?search=${encodeURIComponent(cleanQuery)}&limit=50&autocomplete=true`
            ),
            fetch(
              `/api/organizations/other?search=${encodeURIComponent(cleanQuery)}&limit=50&autocomplete=true`
            ),
          ]);
          const myGroupsData = await myGroupsResp.json();
          const otherGroupsData = await otherGroupsResp.json();
          const myGroups = Array.isArray(myGroupsData)
            ? myGroupsData
            : myGroupsData.results || [];
          const otherGroups = Array.isArray(otherGroupsData)
            ? otherGroupsData
            : otherGroupsData.results || [];
          const seenGroupIds = new Set();
          groups = [...myGroups, ...otherGroups].filter((g) => {
            if (!g || !g.id) return false;
            if (seenGroupIds.has(g.id)) return false;
            seenGroupIds.add(g.id);
            return true;
          });
        } catch (e) {
          if (import.meta.env.MODE === "development")
            console.warn("Group search (fallback branch) failed", e);
        }
      }
      commit("setSearchResults", { users, posts, groups });
      if (!users.length && !posts.length && !groups.length) {
        commit("setNoSearchResults", originalQuery);
      } else {
        commit("setNoSearchResults", null);
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
      commit("clearSearchResults");
      commit("setNoSearchResults", null);
    }
  },
  /** Update the current global search box text. */
  updateSearchText({ commit }, text) {
    commit("setSearchText", text);
  },
  /** Empty out a named collection (e.g. when leaving a search/filter view). */
  clearCollection({ commit }, collectionName) {
    commit("clearCollection", collectionName);
  },

  /** Fetch a page of a user's unread posts (`params.id`, `params.offset`, `params.limit`). */
  async addUserUnreadPosts({ commit }, params) {
    if (!params || !params.id) {
      console.error("Missing required parameters for fetchUserUnreadPosts");
      return { results: [] };
    }

    try {
      const apiParams = {
        authorUserId: params.id,
        offset: params.offset || 0,
        limit: params.limit || 15,
      };

      const response = await fetchUserUnreadPosts(apiParams);

      if (response.statusCode) {
        authErrorHandler(response);
        return null;
      }

      if (response && response.results) {
        commit("setUnreadPosts", response.results);
        if (response.count) {
          commit("setTotalCountPosts", response.count, { root: true });
        }
      }
      return response;
    } catch (error) {
      console.error("Error fetching unread posts:", error);
      return { results: [] };
    }
  },
  /** Fetch onboarding/intro posts shown to new users. */
  async fetchIntroPosts({ commit }) {
    let response = await fetch(`/api/posts/intro`);
    if (response.status === 403 || response.status === 401) {
      const data = await response.json().catch(() => ({}));
      if (
        data?.message === "User account is deleted" ||
        data?.message === "User no longer exists" ||
        data?.message === "Unauthorized"
      ) {
        triggerSessionExpired();
        return;
      }
    }
    let posts = await response.json();
    if (posts.statusCode) {
      authErrorHandler(posts);
    }
    commit("setIntroPosts", posts);
  },

  /** Fetch unread posts for the given params, alternate to `addUserUnreadPosts`. */
  async fetchUnreadPosts({ commit }, params) {
    try {
      const response = await fetchUserUnreadPosts(params);
      if (response && response.results) {
        commit("setUnreadPosts", response.results);
        if (response.count) {
          commit("setTotalCountPosts", response.count, { root: true });
        }
      }
      return response;
    } catch (error) {
      console.error("Error fetching unread posts:", error);
      return { results: [] };
    }
  },

  // Note: clearCollection action implemented above commits the 'clearCollection' mutation.
};
